/*
 * Custom product page enhancements.
 * Keeps theme overrides out of product-info.js for easier theme updates.
 */
(function () {
  'use strict';

  function getSelectedVariant(productInfo) {
    const script =
      productInfo.querySelector('variant-selects [data-selected-variant]') ||
      productInfo.querySelector('[data-selected-variant]');

    if (!script?.textContent?.trim()) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Failed to parse selected variant:', error);
      return null;
    }
  }

  function formatButtonPriceHtml(variant, quantity) {
    const unitPrice = variant.price || 0;
    const compareAtPrice = variant.compare_at_price || 0;
    const totalPrice = unitPrice * quantity;
    const totalCompare = compareAtPrice > unitPrice ? compareAtPrice * quantity : 0;

    const formattedPrice =
      totalPrice === 0
        ? window.theme.strings.free
        : window.theme.formatMoney(totalPrice, theme.moneyFormat);

    if (totalCompare) {
      return `${formattedPrice} <s>${window.theme.formatMoney(totalCompare, theme.moneyFormat)}</s>`;
    }

    return formattedPrice;
  }

  function updateButtonPrice(productInfo) {
    const variant = getSelectedVariant(productInfo);
    if (!variant) return;

    const quantityInput = productInfo.querySelector('[data-quantity-input]');
    const quantity = (quantityInput && parseInt(quantityInput.value || '1', 10)) || 1;
    const priceHtml = formatButtonPriceHtml(variant, quantity);

    const priceElements = [
      ...productInfo.querySelectorAll('[data-add-to-cart] [data-product-price]'),
      ...productInfo.querySelectorAll('cart-bar [data-product-price]'),
    ];

    if (!priceElements.length) {
      const fallback = productInfo.querySelector('[data-product-price]');
      if (fallback) priceElements.push(fallback);
    }

    priceElements.forEach((element) => {
      element.innerHTML = priceHtml;
    });
  }

  function onQuantityChange(event) {
    if (!event.target.matches('[data-quantity-input]')) return;

    const productInfo = event.target.closest('product-info');
    if (!productInfo) return;

    updateButtonPrice(productInfo);
  }

  function getProductVariants(productInfo) {
    const script = productInfo.querySelector('[data-product-variants]');

    if (!script?.textContent?.trim()) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Failed to parse product variants:', error);
      return null;
    }
  }

  function decodeOptionValue(value) {
    if (!value) return value;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function getSelectedOptions(variantSelects) {
    const selected = [];

    variantSelects.querySelectorAll('[data-option-position]').forEach((wrapper) => {
      const position = parseInt(wrapper.dataset.optionPosition, 10) - 1;
      const checked = wrapper.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      const popout = wrapper.querySelector('[data-popout-input]');

      if (checked) {
        selected[position] = decodeOptionValue(checked.value);
      } else if (popout?.value) {
        selected[position] = decodeOptionValue(popout.value);
      }
    });

    return selected;
  }

  function isOptionValueAvailable(variants, optionIndex, optionValue, selectedOptions) {
    const normalizedValue = decodeOptionValue(optionValue);

    return variants.some((variant) => {
      if (!variant.available) return false;
      if (variant.options[optionIndex] !== normalizedValue) return false;

      return selectedOptions.every((selected, index) => {
        if (index === optionIndex || selected == null || selected === '') return true;
        return variant.options[index] === selected;
      });
    });
  }

  function isMaterialOptionWrapper(wrapper) {
    return wrapper?.dataset.optionMaterial === 'true';
  }

  function isColorOptionWrapper(wrapper) {
    return wrapper?.dataset.optionColor === 'true';
  }

  function shouldHideOosNonColorOptions(variantSelects) {
    return variantSelects?.dataset.hideOosNonColorOptions !== 'false';
  }

  function getOptionIndex(wrapper) {
    return parseInt(wrapper.dataset.optionPosition, 10) - 1;
  }

  function getColorOptionIndex(variantSelects) {
    const colorWrapper = variantSelects?.querySelector('[data-option-color="true"]');
    return colorWrapper ? getOptionIndex(colorWrapper) : -1;
  }

  function isMaterialOptionValueAvailable(
    variants,
    optionIndex,
    optionValue,
    selectedOptions = [],
    colorIndex = -1
  ) {
    const normalizedValue = decodeOptionValue(optionValue);

    return variants.some((variant) => {
      if (!variant.available || variant.options[optionIndex] !== normalizedValue) return false;
      if (colorIndex < 0 || colorIndex === optionIndex) return true;

      const selectedColor = selectedOptions[colorIndex];
      return !selectedColor || variant.options[colorIndex] === selectedColor;
    });
  }

  function findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      if (!variant.available) return false;

      return selectedOptions.every((selected, index) => {
        if (selected == null || selected === '') return true;
        return variant.options[index] === selected;
      });
    });
  }

  function isSampleVariant(variant) {
    return variant?.title?.toLowerCase().includes('sample') === true;
  }

  function findFirstAvailableVariantForMaterial(variants, materialIndex, materialValue) {
    const normalizedValue = decodeOptionValue(materialValue);

    const matchesMaterial = (variant) =>
      variant.available && variant.options[materialIndex] === normalizedValue;

    return (
      variants.find((variant) => matchesMaterial(variant) && !isSampleVariant(variant)) ||
      variants.find(matchesMaterial)
    );
  }

  function findFirstAvailableVariantForMaterialAndColor(
    variants,
    materialIndex,
    materialValue,
    colorIndex,
    colorValue
  ) {
    const normalizedMaterialValue = decodeOptionValue(materialValue);
    const normalizedColorValue = decodeOptionValue(colorValue);

    const matchesMaterialAndColor = (variant) =>
      variant.available &&
      variant.options[materialIndex] === normalizedMaterialValue &&
      variant.options[colorIndex] === normalizedColorValue;

    return (
      variants.find((variant) => matchesMaterialAndColor(variant) && !isSampleVariant(variant)) ||
      variants.find(matchesMaterialAndColor)
    );
  }

  function getSwatchContainer(input) {
    return input.closest('tooltip-component.swatch__button, .swatch__button');
  }

  function getOptionContainer(input) {
    return getSwatchContainer(input) || input.closest('.radio__button');
  }

  function setOptionUnavailableState(input, unavailable) {
    input.disabled = unavailable;
    input.classList.toggle('visually-disabled', unavailable);
    input.classList.toggle('disabled', unavailable);
    input.classList.toggle('sold-out', unavailable);

    const container = getSwatchContainer(input);
    if (container) {
      container.classList.toggle('swatch__button--unavailable', unavailable);
    }
  }

  function setInputOptionHiddenState(input, hidden) {
    const container = getOptionContainer(input);
    if (container) {
      container.hidden = hidden;
      container.classList.toggle('hidden', hidden);
    }
  }

  function setPopoutOptionUnavailableState(option, unavailable, hidden) {
    option.classList.toggle('unavailable', unavailable);

    const listItem = option.closest('.select-popout__item');
    if (listItem) {
      listItem.hidden = hidden;
      listItem.classList.toggle('hidden', hidden);
    }
  }

  function selectFirstAvailableOption(wrapper) {
    const firstAvailable = wrapper.querySelector(
      '.swatch-input__input:not([disabled]), .radio__buttons input[type="radio"]:not([disabled])'
    );

    if (firstAvailable) {
      firstAvailable.checked = true;
      firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const firstAvailablePopout = Array.from(
      wrapper.querySelectorAll('[data-popout-option]:not(.unavailable):not(.sold-out)')
    ).find((option) => !option.closest('.select-popout__item')?.hidden);
    const popout = wrapper.querySelector('[data-popout-input]');

    if (!firstAvailablePopout || !popout) return false;

    setOptionValueInPopout(wrapper, firstAvailablePopout.dataset.value);
    popout.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function getOptionValueInput(wrapper, value) {
    const normalizedValue = decodeOptionValue(value);
    const inputs = wrapper.querySelectorAll('.swatch-input__input, .radio__buttons input[type="radio"]');

    return Array.from(inputs).find((input) => decodeOptionValue(input.value) === normalizedValue);
  }

  function setOptionValueInPopout(wrapper, value) {
    const popout = wrapper.querySelector('[data-popout-input]');
    if (!popout) return false;

    const normalizedValue = decodeOptionValue(value);
    const option = Array.from(wrapper.querySelectorAll('[data-popout-option]')).find(
      (item) => decodeOptionValue(item.dataset.value) === normalizedValue
    );
    const optionValue = option?.dataset.value ?? value;
    const listItem = option?.closest('li');

    popout.value = optionValue;
    if (listItem?.dataset.optionValueId) popout.dataset.optionValueId = listItem.dataset.optionValueId;
    if (listItem?.dataset.productUrl) popout.dataset.productUrl = listItem.dataset.productUrl;
    popout.removeAttribute('disabled');

    const currentItem = wrapper.querySelector('.select-popout__item.is-active');
    if (currentItem) currentItem.classList.remove('is-active');
    if (listItem) listItem.classList.add('is-active');

    const currentOption = wrapper.querySelector('[data-popout-option][aria-current]');
    if (currentOption) currentOption.removeAttribute('aria-current');
    if (option) option.setAttribute('aria-current', 'true');

    const toggleText = wrapper.querySelector('[data-popout-toggle-text]');
    if (toggleText) {
      toggleText.innerHTML = optionValue;
      if (toggleText.getAttribute('data-popout-toggle-text') !== null) {
        toggleText.setAttribute('data-popout-toggle-text', optionValue);
      }
    }

    return Boolean(option);
  }

  function setOptionValueInWrapper(wrapper, value) {
    const input = getOptionValueInput(wrapper, value);

    if (input) {
      input.checked = true;
      return true;
    }

    return setOptionValueInPopout(wrapper, value);
  }

  function updateVariantOptionAvailability(productInfo) {
    const variants = getProductVariants(productInfo);
    const variantSelects = productInfo.querySelector('variant-selects');

    if (!variants?.length || !variantSelects) return;

    const selectedOptions = getSelectedOptions(variantSelects);
    const colorIndex = getColorOptionIndex(variantSelects);

    variantSelects.querySelectorAll('[data-option-position]').forEach((wrapper) => {
      const optionIndex = getOptionIndex(wrapper);
      const isMaterialOption = isMaterialOptionWrapper(wrapper);
      const hideUnavailableOption =
        shouldHideOosNonColorOptions(variantSelects) && !isColorOptionWrapper(wrapper);
      let wrapperNeedsReselection = false;

      wrapper
        .querySelectorAll('.swatch-input__input, .radio__buttons input[type="radio"]')
        .forEach((input) => {
          const available = isMaterialOption
            ? isMaterialOptionValueAvailable(
                variants,
                optionIndex,
                input.value,
                selectedOptions,
                colorIndex
              )
            : isOptionValueAvailable(variants, optionIndex, input.value, selectedOptions);

          const unavailable = !available;
          const hidden = hideUnavailableOption && unavailable;

          setOptionUnavailableState(input, unavailable);
          setInputOptionHiddenState(input, hidden);

          if (input.checked && unavailable && (!isMaterialOption || hidden)) {
            input.checked = false;
            wrapperNeedsReselection = true;
          }
        });

      wrapper.querySelectorAll('[data-popout-option]').forEach((option) => {
        const available = isMaterialOption
          ? isMaterialOptionValueAvailable(
              variants,
              optionIndex,
              option.dataset.value,
              selectedOptions,
              colorIndex
            )
          : isOptionValueAvailable(variants, optionIndex, option.dataset.value, selectedOptions);
        const unavailable = !available;
        const hidden = hideUnavailableOption && unavailable;

        setPopoutOptionUnavailableState(option, unavailable, hidden);

        if (option.getAttribute('aria-current') === 'true' && unavailable) {
          wrapperNeedsReselection = true;
        }
      });

      if (wrapperNeedsReselection) {
        selectFirstAvailableOption(wrapper);
      }
    });
  }

  function handleMaterialOptionChange(event, productInfo) {
    const materialWrapper = event.target.closest('[data-option-material="true"]');
    if (!materialWrapper) return;

    const variants = getProductVariants(productInfo);
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variants?.length || !variantSelects) return;

    const selectedOptions = getSelectedOptions(variantSelects);
    if (findMatchingVariant(variants, selectedOptions)) return;

    const materialIndex = getOptionIndex(materialWrapper);
    const materialValue = selectedOptions[materialIndex];
    const colorWrapper = variantSelects.querySelector('[data-option-color="true"]');
    const colorIndex = colorWrapper ? getOptionIndex(colorWrapper) : -1;
    const colorValue = selectedOptions[colorIndex];
    const colorMatchedVariant =
      colorIndex >= 0 && colorIndex !== materialIndex && colorValue
        ? findFirstAvailableVariantForMaterialAndColor(
            variants,
            materialIndex,
            materialValue,
            colorIndex,
            colorValue
          )
        : null;
    const fallbackVariant = colorMatchedVariant || findFirstAvailableVariantForMaterial(
      variants,
      materialIndex,
      materialValue
    );

    if (!fallbackVariant) return;

    variantSelects.querySelectorAll('[data-option-position]').forEach((wrapper) => {
      if (wrapper === materialWrapper) return;

      const optionIndex = getOptionIndex(wrapper);
      setOptionValueInWrapper(wrapper, fallbackVariant.options[optionIndex]);
    });
  }

  function onVariantOptionClick(event) {
    const unavailableTarget = event.target.closest(
      '.swatch__button--unavailable, .select-popout__option.unavailable, .select-popout__option.sold-out'
    );

    if (unavailableTarget) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const label = event.target.closest('label[for]');
    if (!label) return;

    const input = document.getElementById(label.htmlFor);
    if (!input || !input.disabled) return;

    event.preventDefault();
    event.stopPropagation();
  }

  function initVariantOptionAvailability(productInfo) {
    if (productInfo.dataset.variantOptionAvailabilityInit === 'true') return;

    productInfo.dataset.variantOptionAvailabilityInit = 'true';
    productInfo.addEventListener('click', onVariantOptionClick, true);
    productInfo.addEventListener(
      'change',
      (event) => handleMaterialOptionChange(event, productInfo),
      true
    );
    productInfo.addEventListener('change', (event) => {
      if (
        !event.target.matches(
          '.swatch-input__input, .radio__buttons input[type="radio"], [data-popout-input]'
        )
      ) {
        return;
      }

      updateVariantOptionAvailability(productInfo);
    });
    updateVariantOptionAvailability(productInfo);
  }

  function initProductInfo(productInfo) {
    if (productInfo.dataset.customProductPriceInit === 'true') return;

    productInfo.dataset.customProductPriceInit = 'true';
    productInfo.addEventListener('change', onQuantityChange);
    updateButtonPrice(productInfo);
    initVariantOptionAvailability(productInfo);
  }

  function initVariantOptionAvailabilityForAll() {
    document.querySelectorAll('product-info').forEach(initVariantOptionAvailability);
  }

  const HIDE_DELAY_MS = 1000;
  const SWATCH_TOOLTIP_SELECTOR =
    'tooltip-component.swatch__button[data-tooltip], tooltip-component.swatches[data-tooltip]';

  let hideTimeoutId = null;

  function isMobileTouch() {
    return Boolean(window.theme?.touch || window.theme?.isMobile);
  }

  function closeActiveTooltip() {
    document.dispatchEvent(
      new CustomEvent('theme:tooltip:close', {
        bubbles: true,
        detail: { hideTransition: true },
      })
    );
  }

  function scheduleTooltipHide() {
    if (!isMobileTouch()) return;

    clearTimeout(hideTimeoutId);
    hideTimeoutId = setTimeout(closeActiveTooltip, HIDE_DELAY_MS);
  }

  function getSwatchTooltip(target) {
    const element = target instanceof Element ? target : target?.parentElement;
    return element?.closest?.(SWATCH_TOOLTIP_SELECTOR) ?? null;
  }

  function onSwatchActivate(event) {
    if (!isMobileTouch()) return;
    if (!getSwatchTooltip(event.target)) return;

    scheduleTooltipHide();
  }

  function initSwatchTooltipAutoHide() {
    document.addEventListener('click', onSwatchActivate, true);
    document.addEventListener('touchend', onSwatchActivate, { passive: true, capture: true });
    document.addEventListener(
      'change',
      (event) => {
        if (!isMobileTouch()) return;
        if (!event.target.matches?.('.swatch-input__input')) return;

        scheduleTooltipHide();
      },
      true
    );
  }

  const SHOPIFY_INSTALLMENTS_PARAGRAPH_SELECTOR = '#shopify-installments > p';
  const SHOPIFY_INSTALLMENTS_SELECTOR = '#shopify-installments';
  const shopifyInstallmentsObservedRoots = new WeakSet();

  function getShopifyInstallmentsRoots(root) {
    const roots = [root];

    root.querySelectorAll('*').forEach((element) => {
      if (!element.shadowRoot) return;
      roots.push(...getShopifyInstallmentsRoots(element.shadowRoot));
    });

    return roots;
  }

  function applyShopifyInstallmentsLayout() {
    getShopifyInstallmentsRoots(document).forEach((root) => {
      root.querySelectorAll(SHOPIFY_INSTALLMENTS_SELECTOR).forEach((installments) => {
        installments.style.setProperty('display', 'flex', 'important');
        installments.style.setProperty('flex-direction', 'column', 'important');
        installments.style.setProperty('align-items', 'flex-start', 'important');
        installments.style.setProperty('justify-content', 'flex-start', 'important');
      });

      root.querySelectorAll(SHOPIFY_INSTALLMENTS_PARAGRAPH_SELECTOR).forEach((paragraph) => {
        paragraph.style.setProperty('display', 'flex', 'important');
        paragraph.style.setProperty('flex-direction', 'column', 'important');
        paragraph.style.setProperty('align-items', 'flex-start', 'important');
        paragraph.style.setProperty('justify-content', 'flex-start', 'important');
      });
    });
  }

  function observeShopifyInstallmentsRoot(root) {
    if (shopifyInstallmentsObservedRoots.has(root)) return;

    const observer = new MutationObserver(() => {
      applyShopifyInstallmentsLayout();
      observeShopifyInstallmentsRoots();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    shopifyInstallmentsObservedRoots.add(root);
  }

  function observeShopifyInstallmentsRoots() {
    getShopifyInstallmentsRoots(document).forEach(observeShopifyInstallmentsRoot);
  }

  function initShopifyInstallmentsLayout() {
    applyShopifyInstallmentsLayout();
    observeShopifyInstallmentsRoots();

    queueMicrotask(applyShopifyInstallmentsLayout);
    requestAnimationFrame(applyShopifyInstallmentsLayout);
    window.setTimeout(applyShopifyInstallmentsLayout, 50);
    window.setTimeout(applyShopifyInstallmentsLayout, 250);
  }

  function boot() {
    document.querySelectorAll('product-info').forEach(initProductInfo);
    initVariantOptionAvailabilityForAll();
    initShopifyInstallmentsLayout();
  }

  function initThemeSubscriptions() {
    if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS) return;

    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(() => {
        document.querySelectorAll('product-info').forEach((productInfo) => {
          updateButtonPrice(productInfo);
          updateVariantOptionAvailability(productInfo);
        });
      });
    });

    window.subscribe(window.theme.PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
      const productInfo = data?.target?.closest?.('product-info');
      if (!productInfo) return;

      updateVariantOptionAvailability(productInfo);
    });
  }

  document.addEventListener('theme:product-info:loaded', (event) => {
    if (!event.target?.matches?.('product-info')) return;

    initProductInfo(event.target);
    initVariantOptionAvailability(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      initThemeSubscriptions();
    });
  } else {
    boot();
    initThemeSubscriptions();
  }

  initSwatchTooltipAutoHide();
})();
